import React, { useState, useRef, ChangeEvent } from "react";
import { IValidator, randomId, IExpandableInformation, ILabelAndLabelInformation } from "@sebgroup/extract";
import classNames from "classnames";
import { RadioButtonProps } from "@sebgroup/green-react";
import GreenFormItem from "../GreenFormItem";
import { useIsMobile } from "../../../hooks/mediaHooks";

export interface ToggleSelectorGroupProps extends IExpandableInformation, ILabelAndLabelInformation {
  id?: string;
  label?: string;
  valueSelected?: string; // use for single-select (radio buttons)
  valuesSelected?: string[]; // use for multi-select (checkboxes)
  defaultSelected?: string;
  validator?: IValidator;
  onChangeRadio?: (value: string) => string;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  name?: string;
  horizontal?: boolean;
  maxColumns?: number;
  className?: string;
  slideoutButtonLabel?: string;
  onSlideoutButtonClick?: () => void;
}

export const ToggleSelectorGroup = ({
  id,
  defaultSelected,
  valueSelected = defaultSelected,
  valuesSelected = [],
  label,
  labelInformation,
  expandableInfo,
  expandableInfoButtonLabel,
  validator,
  onChangeRadio,
  onChange,
  name: propName = randomId(),
  horizontal,
  maxColumns = 4,
  className,
  slideoutButtonLabel,
  onSlideoutButtonClick,
  children,
}: React.PropsWithChildren<ToggleSelectorGroupProps>) => {
  const [selected, setSelected] = useState<string | string[] | undefined>(valuesSelected?.length > 0 ? valuesSelected : valueSelected);
  const [prevValueSelected, setPrevValueSelected] = useState(valueSelected);
  const [prevValuesSelected, setPrevValuesSelected] = useState(valuesSelected);
  const name = propName;
  const uniqueId = randomId();
  const isMobile = useIsMobile();

  if (valueSelected !== prevValueSelected) {
    setSelected(valueSelected);
    setPrevValueSelected(valueSelected);
  } else if (JSON.stringify(valuesSelected) !== JSON.stringify(prevValuesSelected)) {
    setSelected(valuesSelected);
    setPrevValuesSelected(valuesSelected);
  }

  const handleOnChange = (event: ChangeEvent<HTMLInputElement>) => {
    if (event.target.value !== selected) {
      if (onChangeRadio) {
        onChangeRadio(event.target.value);
      }
      if (onChange) {
        onChange(event);
      }
    }
  };

  const radioBtnRef: React.RefObject<HTMLInputElement> = useRef(null);

  React.useEffect(() => {
    if (radioBtnRef && radioBtnRef.current) {
      const form: HTMLFormElement = radioBtnRef?.current?.form as HTMLFormElement;
      const resetListener = () => {
        setSelected(undefined);
      };
      form?.addEventListener("reset", resetListener);
      return () => form?.removeEventListener("reset", resetListener);
    } else {
      return () => {};
    }
  }, []);

  const labelFromTitle = label;
  const labelInformationFromDescription = labelInformation;

  const formItemProps = {
    validator,
    labelInformation: labelInformationFromDescription,
    label: labelFromTitle,
    expandableInfo,
    expandableInfoButtonLabel,
    role: "radiogroup",
  };

  const radioGroupWrapperClassNames = classNames(
    "gds-radio-group-wrapper",
    {
      "gds-radio-group-wrapper--horizontal": horizontal,
    },
    className
  );

  const describedBy = classNames({
    [`${uniqueId}_message`]: validator?.message !== undefined && validator.message.length > 0,
    [`${uniqueId}_info`]: labelInformation,
    [`gds-expandable-info-${uniqueId}`]: expandableInfo,
  });

  const radioGroupStyle = {
    gridTemplateColumns: horizontal && !isMobile ? `repeat(${maxColumns}, 1fr)` : "1fr",
  };

  return (
    <GreenFormItem {...formItemProps} inputId={uniqueId} slideoutButtonLabel={slideoutButtonLabel} onSlideoutButtonClick={onSlideoutButtonClick} name={name}>
      <div className={radioGroupWrapperClassNames} style={radioGroupStyle}>
        {React.Children.map(children as React.ReactElement, (radioButton: React.ReactElement<RadioButtonProps>) => {
          const isChecked = Array.isArray(selected) ? selected.includes(radioButton.props.value) : selected === radioButton.props.value;

          return React.isValidElement<React.FC<RadioButtonProps>>(radioButton)
            ? React.cloneElement(radioButton, {
                "aria-describedby": describedBy,
                onChange: handleOnChange,
                checked: isChecked,
                name,
                ref: radioBtnRef,
                testId: `${id}-option-${radioButton.key}__testid`,
              })
            : radioButton;
        })}
      </div>
    </GreenFormItem>
  );
};

export default ToggleSelectorGroup;
